/**
 * VendorJourneyPanel — vendor-facing respondent journey view.
 *
 * Deliberately a net-new page, not a trim of JourneyInsights.jsx: a
 * hardcoded chart/column list here means a future change to the internal
 * page can't accidentally widen what a vendor sees. Calls only
 * /api/vendor-views/* — the backend tile-entitlement gate (see
 * server/middleware/enforceTileEntitlement.js) is the real boundary that
 * keeps this to journey-shaped data; this page just renders what that
 * router is willing to serve.
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import dayjs from 'dayjs';
import {
  BarChart, Bar, AreaChart, Area, Tooltip,
  XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_URL } from '../runtimeConfig';
import { useAuth } from '../context/AuthContext';

ModuleRegistry.registerModules([AllCommunityModule]);

const TEAL = '#008080';
const AMBER = '#FFC107';
const AXIS_TICK = { fill: '#ffffff', fontSize: 12 };

function pct(num, den) {
  if (!den) return '—';
  return Math.round((num / den) * 100);
}

const GRID_COLS = [
  { field: 'responder_id', headerName: 'Responder ID', width: 200, pinned: 'left' },
  { field: 'first_portal_access', headerName: 'First Access', width: 160,
    valueFormatter: (p) => (p.value ? dayjs(p.value).format('DD MMM YY HH:mm') : '—') },
  { field: 'last_portal_access', headerName: 'Last Access', width: 160,
    valueFormatter: (p) => (p.value ? dayjs(p.value).format('DD MMM YY HH:mm') : '—') },
  { field: 'occasions_with_any_data', headerName: 'Responses Rcvd', width: 150 },
  { field: 'occasions_completed', headerName: 'Blocks Completed', width: 160 },
  { field: 'last_completion_at', headerName: 'Last Complete', width: 160,
    valueFormatter: (p) => (p.value ? dayjs(p.value).format('DD MMM YY HH:mm') : '—') },
  { field: 'overall_journey_status', headerName: 'Status', width: 180,
    valueFormatter: (p) => (p.value || '').replace(/_/g, ' ') || '—' },
];

export default function VendorJourneyPanel() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState('');
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [kpis, setKpis] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [daily, setDaily] = useState([]);
  const [grid, setGrid] = useState([]);

  const getHeaders = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const headers = await getHeaders();
      const res = await axios.get(`${API_URL}/api/vendor-views/projects`, { headers });
      if (res.data.success) setProjects(res.data.projects || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setProjectsLoaded(true);
    }
  }, [getHeaders]);

  React.useEffect(() => { loadProjects(); }, [loadProjects]);

  const loadData = useCallback(async (proj) => {
    if (!proj) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const base = `${API_URL}/api/vendor-views`;
      const [f, d, g] = await Promise.all([
        axios.get(`${base}/journey-funnel/${proj}`, { headers }),
        axios.get(`${base}/journey-daily-engagement/${proj}`, { headers }),
        axios.get(`${base}/journey-responder-grid/${proj}`, { headers }),
      ]);
      setKpis(f.data.data.kpis);
      setFunnel(f.data.data.funnel);
      setDaily(d.data.data);
      setGrid(g.data.data || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const hasData = kpis !== null;

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <ArrowBackIcon /> Back to Home
        </BackButton>
        <Title>Respondent Journey</Title>
        <LogoutButton onClick={logout}>Logout</LogoutButton>
      </Header>

      <Content>
        <ControlPanel>
          <FilterLabel>Project:</FilterLabel>
          <FilterSelect value={project} onChange={(e) => setProject(e.target.value)}>
            <option value="">— Select project —</option>
            {projects.map((p) => <option key={p} value={p}>{p}</option>)}
          </FilterSelect>
          <ActionBtn onClick={() => loadData(project)} disabled={!project || loading}>
            {loading ? 'Loading…' : 'Load Data'}
          </ActionBtn>
          <ActionBtn onClick={() => loadData(project)} disabled={!hasData || loading}>
            <RefreshIcon /> Refresh
          </ActionBtn>
        </ControlPanel>

        {error && <ErrorBar>Error: {error}</ErrorBar>}

        {lastUpdated && (
          <StatusBar>Last refreshed: {lastUpdated.toLocaleString()} · Project: {project}</StatusBar>
        )}

        {!hasData && !loading && projectsLoaded && (
          <EmptyState>
            <EmptyIcon>📊</EmptyIcon>
            <EmptyText>Select a project and click "Load Data" to view respondent journey progress.</EmptyText>
          </EmptyState>
        )}

        {loading && (
          <EmptyState>
            <EmptyIcon>⏳</EmptyIcon>
            <EmptyText>Loading journey data…</EmptyText>
          </EmptyState>
        )}

        {hasData && !loading && (
          <>
            <KPIStrip>
              <KPICard>
                <KPILabel>Enrolled</KPILabel>
                <KPIValue>{kpis?.enrolled ?? '—'}</KPIValue>
              </KPICard>
              <KPICard>
                <KPILabel>Accessed Portal</KPILabel>
                <KPIValue>{kpis?.accessed ?? '—'}</KPIValue>
                {kpis && <KPISub>{pct(kpis.accessed, kpis.enrolled)}% of enrolled</KPISub>}
              </KPICard>
              <KPICard>
                <KPILabel>Started a Block</KPILabel>
                <KPIValue>{kpis?.started ?? '—'}</KPIValue>
                {kpis && <KPISub>{pct(kpis.started, kpis.enrolled)}% of enrolled</KPISub>}
              </KPICard>
              <KPICard>
                <KPILabel>Completed 1+ Occasion</KPILabel>
                <KPIValue>{kpis?.completed ?? '—'}</KPIValue>
                {kpis && <KPISub>{pct(kpis.completed, kpis.enrolled)}% of enrolled</KPISub>}
              </KPICard>
            </KPIStrip>

            <ChartRow>
              <ChartCard>
                <ChartTitle>Engagement Funnel</ChartTitle>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={funnel} layout="vertical" margin={{ top: 5, right: 60, left: 0, bottom: 5 }}>
                    <XAxis type="number" domain={[0, 'dataMax']} allowDataOverflow tick={AXIS_TICK} />
                    <YAxis type="category" dataKey="stage" width={190} tick={AXIS_TICK} />
                    <Tooltip formatter={(v, n, p) => [`${v} (${p.payload.pct_of_enrolled}%)`, 'Responders']} />
                    <Bar dataKey="count" fill={TEAL} radius={[0, 4, 4, 0]} barSize={28}>
                      <LabelList dataKey="count" position="right" style={{ fontSize: 12, fill: '#555' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard>
                <ChartTitle>Daily Engagement</ChartTitle>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={daily} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" angle={-40} textAnchor="end" height={60}
                      tick={{ ...AXIS_TICK, fontSize: 11 }} tickFormatter={(v) => dayjs(v).format('DD MMM')} />
                    <YAxis tick={AXIS_TICK} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="portal_accesses" name="Portal Access" fill={TEAL} stroke={TEAL} fillOpacity={0.3} />
                    <Area type="monotone" dataKey="block_completions" name="Block Completions" fill={AMBER} stroke={AMBER} fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartRow>

            <SectionTitle>Responder Summary</SectionTitle>
            <GridWrap>
              <AgGridReact
                theme={themeQuartz}
                rowData={grid}
                columnDefs={GRID_COLS}
                defaultColDef={{ resizable: true, sortable: true, filter: true }}
                domLayout="autoHeight"
                pagination
                paginationPageSize={50}
              />
            </GridWrap>
          </>
        )}
      </Content>
    </Container>
  );
}

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background-color: ${(p) => p.theme.colors.background};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.md};
  background: linear-gradient(135deg, ${(p) => p.theme.colors.primary} 0%, ${(p) => p.theme.colors.primaryDark} 100%);
  padding: ${(p) => p.theme.spacing.lg} ${(p) => p.theme.spacing.xl};
  box-shadow: ${(p) => p.theme.shadows.md};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.1);
  color: ${(p) => p.theme.colors.textPrimary};
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  border-radius: ${(p) => p.theme.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: ${(p) => p.theme.fontSizes.sm};
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.2); }
`;

const Title = styled.h1`
  flex: 1;
  font-family: ${(p) => p.theme.fonts.heading};
  font-size: ${(p) => p.theme.fontSizes['2xl']};
  font-weight: ${(p) => p.theme.fontWeights.bold};
  color: ${(p) => p.theme.colors.textPrimary};
  margin: 0;
`;

const LogoutButton = styled.button`
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  background-color: rgba(255, 255, 255, 0.1);
  color: ${(p) => p.theme.colors.textPrimary};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${(p) => p.theme.borderRadius.md};
  font-size: ${(p) => p.theme.fontSizes.sm};
  cursor: pointer;
  &:hover { background-color: rgba(255, 255, 255, 0.2); }
`;

const Content = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${(p) => p.theme.spacing.xl};
`;

const ControlPanel = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.sm};
  flex-wrap: wrap;
  padding: ${(p) => p.theme.spacing.md};
  background: ${(p) => p.theme.colors.cardBackground};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  border: 1px solid ${(p) => p.theme.colors.border};
  margin-bottom: ${(p) => p.theme.spacing.md};
`;

const FilterLabel = styled.label`
  font-size: ${(p) => p.theme.fontSizes.sm};
  font-weight: ${(p) => p.theme.fontWeights.medium};
  color: ${(p) => p.theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  border-radius: ${(p) => p.theme.borderRadius.md};
  border: 1px solid ${(p) => p.theme.colors.border};
  background: white;
  font-size: ${(p) => p.theme.fontSizes.sm};
  min-width: 180px;
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  border-radius: ${(p) => p.theme.borderRadius.md};
  border: none;
  font-size: ${(p) => p.theme.fontSizes.sm};
  font-weight: ${(p) => p.theme.fontWeights.semibold};
  cursor: pointer;
  background: ${(p) => p.theme.colors.primary};
  color: white;
  &:hover:not(:disabled) { opacity: 0.88; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const StatusBar = styled.div`
  font-size: ${(p) => p.theme.fontSizes.sm};
  color: ${(p) => p.theme.colors.textSecondary};
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  background: ${(p) => p.theme.colors.cardBackground};
  border-radius: ${(p) => p.theme.borderRadius.md};
  border: 1px solid ${(p) => p.theme.colors.border};
  margin-bottom: ${(p) => p.theme.spacing.md};
`;

const ErrorBar = styled.div`
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  background: #fef2f2;
  color: #dc2626;
  border-radius: ${(p) => p.theme.borderRadius.md};
  margin-bottom: ${(p) => p.theme.spacing.md};
  font-size: ${(p) => p.theme.fontSizes.sm};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(p) => p.theme.spacing['3xl']};
  background: ${(p) => p.theme.colors.cardBackground};
  border-radius: ${(p) => p.theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`font-size: 56px; margin-bottom: ${(p) => p.theme.spacing.md};`;
const EmptyText = styled.p`
  font-size: ${(p) => p.theme.fontSizes.lg};
  color: ${(p) => p.theme.colors.textSecondary};
`;

const KPIStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${(p) => p.theme.spacing.md};
  margin-bottom: ${(p) => p.theme.spacing.lg};
`;

const KPICard = styled.div`
  background: ${(p) => p.theme.colors.cardBackground};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  padding: ${(p) => p.theme.spacing.md};
`;

const KPILabel = styled.div`
  font-size: ${(p) => p.theme.fontSizes.xs};
  color: ${(p) => p.theme.colors.textSecondary};
  font-weight: ${(p) => p.theme.fontWeights.medium};
  margin-bottom: 4px;
`;

const KPIValue = styled.div`
  font-size: ${(p) => p.theme.fontSizes['2xl']};
  font-weight: ${(p) => p.theme.fontWeights.bold};
  color: ${(p) => p.theme.colors.textPrimary};
`;

const KPISub = styled.div`
  font-size: ${(p) => p.theme.fontSizes.xs};
  color: ${(p) => p.theme.colors.textSecondary};
  margin-top: 2px;
`;

const ChartRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${(p) => p.theme.spacing.lg};
  margin-bottom: ${(p) => p.theme.spacing.lg};
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const ChartCard = styled.div`
  background: ${(p) => p.theme.colors.cardBackground};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  padding: ${(p) => p.theme.spacing.lg};
  box-shadow: ${(p) => p.theme.shadows.md};
  border: 1px solid ${(p) => p.theme.colors.border};
`;

const ChartTitle = styled.h3`
  font-size: ${(p) => p.theme.fontSizes.base};
  font-weight: ${(p) => p.theme.fontWeights.semibold};
  color: ${(p) => p.theme.colors.textPrimary};
  margin: 0 0 ${(p) => p.theme.spacing.md} 0;
`;

const SectionTitle = styled.h3`
  font-size: ${(p) => p.theme.fontSizes.lg};
  font-weight: ${(p) => p.theme.fontWeights.semibold};
  color: ${(p) => p.theme.colors.textPrimary};
  margin: 0 0 ${(p) => p.theme.spacing.sm} 0;
`;

const GridWrap = styled.div`
  height: 420px;
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;
